import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { queryClient } from './services/api';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './components/LoginPage';
import { Auth0Callback } from './components/Auth0Callback';
import { Layout } from './components/Layout';
import { NotFound } from './components/NotFound';

// Pages load on first visit, so the login page doesn't download every page and chart library
const Dashboard = lazy(() => import('./components/Dashboard').then(m => ({ default: m.Dashboard })));
const MyTeams = lazy(() => import('./components/MyTeams'));
const PlayerStatsTable = lazy(() => import('./components/PlayerStatsTable'));
const PlayerDetail = lazy(() => import('./components/PlayerDetail'));
const GlobalScoutingReports = lazy(() => import('./components/GlobalScoutingReports'));
const TalentPipeline = lazy(() => import('./components/TalentPipeline').then(m => ({ default: m.TalentPipeline })));
const PlayerComparison = lazy(() => import('./components/PlayerComparison').then(m => ({ default: m.PlayerComparison })));
const GameReports = lazy(() => import('./components/GameReports'));
const TeamDetail = lazy(() => import('./components/TeamDetail'));
const GamedayAnalysis = lazy(() => import('./components/GamedayAnalysis'));
const MatchAnalysis = lazy(() => import('./components/MatchAnalysis'));
const Account = lazy(() => import('./components/Account'));
const Watchlist = lazy(() => import('./components/Watchlist'));
const AskData = lazy(() => import('./components/AskData'));
const AdminSettings = lazy(() => import('./components/AdminSettings'));
const ListManagement = lazy(() => import('./components/list/ListManagement'));
const Guide = lazy(() => import('./components/guide/Guide'));

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useAuth();

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!state.user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route wrapper (redirects to dashboard if logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useAuth();

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (state.user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Page titles by path; detail pages are matched on their first segment
const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/teams': 'My Teams',
  '/players': 'Players',
  '/talent-pipeline': 'Talent Pipeline',
  '/list': 'Team Management',
  '/watchlist': 'My Watch List',
  '/ask': 'Ask the Data',
  '/admin': 'Organisation Settings',
  '/guide': 'How to Use',
  '/scouting-reports': 'Scouting Reports',
  '/player-comparison': 'Player Comparison',
  '/game-reports': 'Game Reports',
  '/gameday-analysis': 'Gameday Analysis',
  '/profile': 'Profile',
  '/settings': 'Settings',
};

const DETAIL_TITLES: Record<string, string> = {
  teams: 'Team Detail',
  players: 'Player Detail',
};

const pageTitleFor = (pathname: string): string | undefined => {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (PAGE_TITLES[path]) return PAGE_TITLES[path];
  if (path.startsWith('/gameday-analysis/match/')) return 'Match Analysis';
  const segments = path.split('/');
  return (segments.length === 3 && DETAIL_TITLES[segments[1]]) || 'Page Not Found';
};

// Shared layout: the sidebar stays mounted and only the page content changes
const AppShell: React.FC = () => {
  const { pathname } = useLocation();
  return (
    <Layout pageTitle={pageTitleFor(pathname)}>
      <Suspense
        fallback={
          <div className="flex justify-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </Layout>
  );
};

function AppRoutes() {
  return (
    <Router>
      <div className="App">
        <Routes>
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/callback" 
            element={<Auth0Callback />} 
          />
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="/teams" element={<MyTeams />} />
            <Route path="/teams/:teamId" element={<TeamDetail />} />
            <Route path="/players" element={<PlayerStatsTable />} />
            <Route path="/players/:playerId" element={<PlayerDetail />} />
            <Route path="/talent-pipeline" element={<TalentPipeline />} />
            <Route path="/list" element={<ListManagement />} />
            <Route path="/watchlist" element={<Watchlist />} />
            <Route path="/ask" element={<AskData />} />
            <Route path="/admin" element={<AdminSettings />} />
            <Route path="/guide" element={<Guide />} />
            <Route path="/scouting-reports" element={<GlobalScoutingReports />} />
            <Route path="/player-comparison" element={<PlayerComparison />} />
            <Route path="/game-reports" element={<GameReports />} />
            <Route path="/gameday-analysis" element={<GamedayAnalysis />} />
            <Route path="/gameday-analysis/match/:gameId" element={<MatchAnalysis />} />
            <Route path="/profile" element={<Account />} />
            <Route path="/settings" element={<Account />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
        <Toaster position="top-right" />
      </div>
    </Router>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
